import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const ClinicContext = createContext(null);

// Convert #RRGGBB -> "H S% L%" for CSS hsl(var(--x))
export function hexToHsl(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

function applyTheme(brandColor) {
  const hsl = hexToHsl(brandColor);
  const root = document.documentElement;
  if (hsl) {
    root.style.setProperty("--primary", hsl);
    root.style.setProperty("--brand", brandColor);
  }
}

export function ClinicProvider({ children }) {
  const [clinic, setClinic] = useState(null);

  const loadBranding = useCallback(async () => {
    try {
      const { data } = await api.get("/clinic/branding");
      setClinic(data);
      applyTheme(data.brand_color);
    } catch (e) {
      // fall back to defaults baked into CSS
    }
  }, []);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const updateClinic = (data) => {
    setClinic(data);
    applyTheme(data.brand_color);
  };

  return (
    <ClinicContext.Provider value={{ clinic, loadBranding, updateClinic }}>
      {children}
    </ClinicContext.Provider>
  );
}

export const useClinic = () => useContext(ClinicContext);
